/* The auth-attempt lifecycle, and the one place that enforces the funnel's
 * invariant:
 *
 *     every AuthStarted is terminated by exactly one
 *     AuthCompleted or AuthProviderFailed.
 *
 * That invariant cannot live in the screens, because an auth attempt is not
 * confined to one page load: Google sign-in is a FULL-PAGE redirect, so the
 * attempt begins in LoginScreen, leaves the document entirely, and is resolved
 * on a later bootstrap in session.tsx. Anything that tracked the terminal event
 * where the attempt started would miss every social outcome — which is exactly
 * how AuthProviderFailed/AuthCompleted came to fire for authWebApp and never
 * for this app.
 *
 * So the attempt is a record in sessionStorage (per-tab, survives the redirect
 * round trip), and every terminal path clears it. Clearing is what makes the
 * event fire at most once; a no-op when no attempt is pending is what stops an
 * ordinary cookie restore from minting a spurious AuthCompleted.
 *
 * Nothing here may throw: it sits directly on the sign-in path, so every entry
 * point is wrapped and sessionStorage access is guarded (it throws outright in
 * some privacy modes rather than returning null). Telemetry must never be able
 * to break a login. */

import {
  flushAnalytics,
  trackAuthCompleted,
  trackAuthProviderFailed,
  trackAuthProviderSucceeded,
  trackAuthStarted,
  type AuthMethod,
  type AuthType,
} from "../analytics/tracking";
import { flushTelemetry, logger } from "../services/telemetry";
import { detectEmbeddedBrowser } from "./embeddedBrowser";

const ATTEMPT_KEY = "auth:attempt";

/** Beyond this an attempt found still pending is treated as abandoned rather
 *  than resolved. sessionStorage already dies with the tab, so this only has to
 *  cover "left the tab open on the consent screen and came back much later" —
 *  generous enough that a slow but genuine Google round trip is never
 *  mis-reported as a failure. */
const MAX_ATTEMPT_AGE_MS = 30 * 60 * 1000;

/** Why an attempt ended without the provider ever answering. These become the
 *  AuthProviderFailed `errorCode`, so they are the discriminator between "the
 *  user changed their mind" and "the browser could never have worked". */
export const NO_RESULT_CODE = "auth/redirect-no-result";
const SUPERSEDED_CODE = "auth/superseded";

interface Attempt {
  id: string;
  authMethod: AuthMethod;
  authType: AuthType;
  startedAt: number;
  /** Whether the provider leg already reported success — distinguishes a
   *  failure at Google from a failure in the identity exchange. */
  providerOk: boolean;
  embedded: boolean;
  kind: string;
  ua: string;
}

/** Correlation id for one attempt.
 *
 *  Deliberately NOT crypto.randomUUID(): that is secure-context-only and absent
 *  from older WebKit and several in-app webviews — precisely the browsers this
 *  instrumentation exists to measure. Using it would throw into the catch below
 *  and produce no telemetry for exactly the cohort under investigation. This id
 *  correlates log lines; it needs uniqueness, not unpredictability. */
function attemptId(): string {
  const uuid = globalThis.crypto?.randomUUID;
  if (typeof uuid === "function") return globalThis.crypto.randomUUID();

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readAttempt(): Attempt | null {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    // Shape-check rather than trust: this is storage the user can edit, and a
    // malformed record must degrade to "no attempt", never throw.
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as Attempt).id === "string" &&
      typeof (parsed as Attempt).startedAt === "number" &&
      typeof (parsed as Attempt).authMethod === "string"
    ) {
      return parsed as Attempt;
    }

    return null;
  } catch {
    return null;
  }
}

function writeAttempt(attempt: Attempt): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
  } catch {
    /* Storage unavailable (privacy mode). The attempt still fires AuthStarted
     * and its terminal event within this page load; only the cross-redirect
     * half is lost. */
  }
}

function clearAttempt(): void {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    /* nothing to clear */
  }
}

function attrs(attempt: Attempt, extra: Record<string, unknown> = {}) {
  return {
    "auth.attempt_id": attempt.id,
    "auth.method": attempt.authMethod,
    "auth.type": attempt.authType,
    "auth.provider_ok": attempt.providerOk,
    "auth.embedded_browser": attempt.embedded,
    "auth.embedded_kind": attempt.kind,
    "auth.user_agent": attempt.ua,
    "auth.elapsed_ms": Date.now() - attempt.startedAt,
    ...extra,
  };
}

/** Opens an attempt: fires AuthStarted and records it so whichever page load
 *  resolves it can fire the matching terminal event.
 *
 *  An attempt already in flight is terminated first (AuthProviderFailed,
 *  `auth/superseded`) rather than dropped — a user who presses Google and then
 *  email must not leave a started event that can never be closed. */
export function beginAuthAttempt(
  authMethod: AuthMethod,
  authType: AuthType,
): void {
  try {
    const pending = readAttempt();
    if (pending) failAttempt(pending, new Error("superseded"), SUPERSEDED_CODE);

    const { embedded, kind } = detectEmbeddedBrowser();
    const attempt: Attempt = {
      id: attemptId(),
      authMethod,
      authType,
      startedAt: Date.now(),
      providerOk: false,
      embedded,
      kind,
      ua: navigator.userAgent,
    };

    writeAttempt(attempt);
    trackAuthStarted(authMethod, authType);
    logger.info("auth attempt started", attrs(attempt));
  } catch {
    /* telemetry must never break a login */
  }
}

/** The provider (Firebase, including the Google round trip) returned a
 *  credential. Non-terminal — the identity exchange still has to land. */
export function markAuthProviderSucceeded(): void {
  try {
    const attempt = readAttempt();
    if (!attempt) return;

    const updated = { ...attempt, providerOk: true };
    writeAttempt(updated);
    trackAuthProviderSucceeded(updated.authMethod, updated.authType);
    logger.info("auth provider succeeded", attrs(updated));
  } catch {
    /* telemetry must never break a login */
  }
}

function failAttempt(
  attempt: Attempt,
  err: unknown,
  fallbackCode?: string,
): void {
  clearAttempt();
  trackAuthProviderFailed(
    attempt.authMethod,
    attempt.authType,
    err,
    fallbackCode,
  );
  logger.warn(
    err instanceof Error ? err : new Error(String(err)),
    "auth attempt failed",
    attrs(attempt, { "auth.error_code": fallbackCode ?? "" }),
  );
}

/** Terminates the attempt as a failure, whichever leg it died on. A no-op when
 *  no attempt is pending, so a duplicated error path cannot double-count. */
export function failAuthAttempt(err: unknown, fallbackCode?: string): void {
  try {
    const attempt = readAttempt();
    if (!attempt) return;

    failAttempt(attempt, err, fallbackCode);
  } catch {
    /* telemetry must never break a login */
  }
}

/** Terminates the attempt as a success. A no-op when no attempt is pending —
 *  which is what keeps a plain cookie restore (every reload of a signed-in
 *  session) from minting an AuthCompleted that no AuthStarted preceded. */
export function completeAuthAttempt(): void {
  try {
    const attempt = readAttempt();
    if (!attempt) return;

    clearAttempt();
    trackAuthCompleted(attempt.authMethod, attempt.authType);
    logger.info("auth attempt completed", attrs(attempt));
  } catch {
    /* telemetry must never break a login */
  }
}

/** Closes an attempt that this page load could not resolve.
 *
 *  Call at the END of bootstrap, once the redirect result and the cookie
 *  restore have both had their chance. A record still pending here means the
 *  browser came back from the provider with nothing — an embedded-webview
 *  refusal or an abandoned consent screen — which is the single largest and,
 *  until now, entirely unrecorded drop in the funnel.
 *
 *  Note the one case that cannot be closed from the browser at all: a user who
 *  leaves for Google and never returns runs no further code here, so their
 *  AuthStarted stays open forever. That is why the departure is logged with the
 *  embedded-browser label at `beginAuthAttempt` — for those users it is the
 *  only record that will ever exist. */
export function reconcileAbandonedAttempt(): void {
  try {
    const attempt = readAttempt();
    if (!attempt) return;

    const stale = Date.now() - attempt.startedAt > MAX_ATTEMPT_AGE_MS;
    failAttempt(
      attempt,
      new Error(
        stale
          ? "auth attempt abandoned (stale)"
          : "provider returned no credential",
      ),
      NO_RESULT_CODE,
    );
  } catch {
    /* telemetry must never break a login */
  }
}

/** Whether an attempt is currently in flight. Exposed for the bootstrap, which
 *  only reconciles when it did not itself resolve one. */
export function hasPendingAuthAttempt(): boolean {
  return readAttempt() !== null;
}

/** How long a pre-navigation flush may hold up the redirect. Long enough for a
 *  single beacon-sized POST on a slow phone, short enough that nobody watches a
 *  dead button: telemetry must never be the reason a sign-in feels broken. */
const FLUSH_BUDGET_MS = 1200;

/** Push queued telemetry out NOW, before a full-page navigation.
 *
 *  Both pipelines batch — the Amplitude SDK by flushIntervalMillis, OTel by the
 *  BatchLogRecordProcessor's scheduled delay — so anything recorded immediately
 *  before signInWithRedirect never leaves the tab. Amplitude persists and
 *  re-sends on the next load, which is why AuthStarted for a Google sign-in
 *  showed up only once the user came back; a user who abandons at Google's
 *  consent screen never comes back, so their attempt was never recorded at all.
 *
 *  Always resolves, and never later than FLUSH_BUDGET_MS: a hung exporter must
 *  not strand the user on the button. */
export async function flushAuthTelemetry(): Promise<void> {
  try {
    await Promise.race([
      Promise.allSettled([flushAnalytics(), flushTelemetry()]),
      new Promise((resolve) => setTimeout(resolve, FLUSH_BUDGET_MS)),
    ]);
  } catch {
    /* telemetry must never break a login */
  }
}
